import React from 'react';
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';

import {setInitialCustomExtensionType} from '../../lib/custom-extension-initial-type';
import styles from './extension-manager.css';

class ExtensionManager extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleExport',
            'handleDelete',
            'handleToggleMenu',
            'handleLoadFromFile',
            'handleLoadFromCode',
            'handleLoadFromURL'
        ]);
        this.state = {
            menuOpen: false,
            refreshKey: 0
        };
    }

    getLoadedExtensions () {
        const {vm} = this.props;
        if (!vm || !vm.extensionManager) return [];

        const extensionURLs = vm.extensionManager.getExtensionURLs();
        const blockInfo = vm.runtime._blockInfo || [];

        const extensions = [];
        for (const [extensionId, serviceName] of vm.extensionManager._loadedExtensions.entries()) {
            const isBuiltin = Object.prototype.hasOwnProperty.call(
                vm.extensionManager.builtinExtensions, extensionId);
            const categoryInfo = blockInfo.find(info => info.id === extensionId);
            const url = extensionURLs[extensionId] || '';

            extensions.push({
                id: extensionId,
                name: (categoryInfo && categoryInfo.name) || extensionId,
                color1: (categoryInfo && categoryInfo.color1) || '#76a9d2',
                color2: (categoryInfo && categoryInfo.color2) || '#76a9d2',
                color3: (categoryInfo && categoryInfo.color3) || '#76a9d2',
                url,
                builtin: isBuiltin
            });
        }
        return extensions;
    }

    async handleExport (extensionId) {
        const {vm} = this.props;
        const extensionURLs = vm.extensionManager.getExtensionURLs();
        const url = extensionURLs[extensionId];
        if (!url) return;

        try {
            const response = await fetch(url);
            const content = await response.text();
            const blob = new Blob([content], {type: 'application/javascript'});
            const downloadUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `${extensionId}.js`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            // eslint-disable-next-line no-alert
            alert(`导出失败: ${err.message}`);
        }
    }

    handleDelete (extensionId) {
        const {vm} = this.props;
        const em = vm.extensionManager;
        const runtime = vm.runtime;

        const serviceName = em._loadedExtensions.get(extensionId);
        if (!serviceName) return;

        const isBuiltin = Object.prototype.hasOwnProperty.call(em.builtinExtensions, extensionId);

        // Save categoryInfo before removing (needed to trigger toolbox refresh)
        const blockInfoIndex = runtime._blockInfo.findIndex(info => info.id === extensionId);
        const removedCategoryInfo = blockInfoIndex !== -1 ?
            runtime._blockInfo[blockInfoIndex] : null;

        // Remove from loaded extensions map
        em._loadedExtensions.delete(extensionId);

        // Remove from worker URLs (only for non-builtin extensions;
        // builtin extensions have no worker, service name format is "extension.EXTENSION_ID")
        if (!isBuiltin) {
            const parts = serviceName.split('.');
            const workerId = +parts[1];
            if (!isNaN(workerId) && em.workerURLs[workerId]) {
                em.workerURLs[workerId] = null;
            }
        }

        // Remove from runtime block info
        if (blockInfoIndex !== -1) {
            runtime._blockInfo.splice(blockInfoIndex, 1);
        }

        // Emit BLOCKSINFO_UPDATE to force blocks.jsx to regenerate the toolbox XML.
        // Since the extension is removed from _blockInfo, the new toolbox XML won't
        // include it, effectively removing the extension's category from the palette.
        if (removedCategoryInfo) {
            runtime.emit('BLOCKSINFO_UPDATE', removedCategoryInfo);
        }

        // Also refresh workspace to update block instances
        vm.refreshWorkspace();

        // Trigger re-render
        this.setState(prevState => ({refreshKey: prevState.refreshKey + 1}));
    }

    handleToggleMenu () {
        this.setState(prevState => ({menuOpen: !prevState.menuOpen}));
    }

    handleLoadFromFile () {
        this.setState({menuOpen: false});
        setInitialCustomExtensionType('file');
        this.props.onOpenCustomExtensionModal();
    }

    handleLoadFromCode () {
        this.setState({menuOpen: false});
        setInitialCustomExtensionType('text');
        this.props.onOpenCustomExtensionModal();
    }

    handleLoadFromURL () {
        this.setState({menuOpen: false});
        setInitialCustomExtensionType('url');
        this.props.onOpenCustomExtensionModal();
    }

    render () {
        // Access refreshKey to force re-render after delete
        const extensions = this.getLoadedExtensions();

        return (
            <div className={styles.managerContainer}>
                {extensions.length === 0 ? (
                    <div className={styles.emptyState}>
                        暂无已加载的扩展
                    </div>
                ) : (
                    <div className={styles.cardList}>
                        {extensions.map(ext => (
                            <div
                                key={ext.id}
                                className={styles.extensionCard}
                                style={{
                                    backgroundColor: ext.color1
                                }}
                            >
                                <div className={styles.cardInfo}>
                                    <div className={styles.extensionName}>
                                        {ext.name}
                                    </div>
                                    <div className={styles.extensionId}>
                                        {ext.id}
                                    </div>
                                </div>
                                <div className={styles.cardActions}>
                                    <button
                                        className={styles.cardButton}
                                        onClick={() => this.handleExport(ext.id)}
                                        title={ext.builtin ? '内置扩展不支持导出' : '导出扩展JS文件'}
                                        disabled={ext.builtin}
                                    >
                                        导出
                                    </button>
                                    <button
                                        className={`${styles.cardButton} ${styles.deleteButton}`}
                                        onClick={() => this.handleDelete(ext.id)}
                                        title="卸载扩展"
                                    >
                                        删除
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Right-bottom "{}" button with dropdown menu */}
                <div className={styles.fabContainer}>
                    {this.state.menuOpen && (
                        <div className={styles.fabMenu}>
                            <div
                                className={styles.fabMenuItem}
                                onClick={this.handleLoadFromFile}
                            >
                                从文件加载扩展
                            </div>
                            <div
                                className={styles.fabMenuItem}
                                onClick={this.handleLoadFromCode}
                            >
                                从代码加载扩展
                            </div>
                            <div
                                className={styles.fabMenuItem}
                                onClick={this.handleLoadFromURL}
                            >
                                从URL加载扩展
                            </div>
                        </div>
                    )}
                    <button
                        className={styles.fabButton}
                        onClick={this.handleToggleMenu}
                        title="加载自定义扩展"
                    >
                        {'{}'}
                    </button>
                </div>
            </div>
        );
    }
}

ExtensionManager.propTypes = {
    vm: PropTypes.shape({
        extensionManager: PropTypes.shape({
            _loadedExtensions: PropTypes.object,
            builtinExtensions: PropTypes.object,
            workerURLs: PropTypes.array,
            getExtensionURLs: PropTypes.func
        }),
        runtime: PropTypes.shape({
            _blockInfo: PropTypes.array
        }),
        refreshWorkspace: PropTypes.func
    }),
    onOpenCustomExtensionModal: PropTypes.func
};

export default ExtensionManager;
